import React, { useEffect, useState } from "react";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Select, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/EditOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import { IndicatorsService } from "services/indicators";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const SOURCE_COLORS = { MINDICADOR: "success", MANUAL: "info", DEFAULT: "warning" };
const fmtN = (n, dec = 0) => n == null ? "—" : Number(n).toLocaleString("es-CL", { maximumFractionDigits: dec });

function EditDialog({ open, onClose, indicator, onSubmit }) {
  const [form, setForm] = useState({ uf: 0, utm: 0, imm: 0, ipc: null });
  useEffect(() => {
    if (indicator) {
      setForm({
        uf: indicator.uf || 0,
        utm: indicator.utm || 0,
        imm: indicator.imm || 0,
        ipc: indicator.ipc ?? "",
      });
    }
  }, [indicator]);

  if (!indicator) return null;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Editar indicadores {MONTHS[indicator.period.month - 1]} {indicator.period.year}</DialogTitle>
      <DialogContent sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, pt: 2 }}>
        <TextField label="UF (CLP)" type="number" value={form.uf} onChange={(e) => setForm({ ...form, uf: Number(e.target.value) })} size="small" />
        <TextField label="UTM (CLP)" type="number" value={form.utm} onChange={(e) => setForm({ ...form, utm: Number(e.target.value) })} size="small" />
        <TextField label="IMM (CLP)" type="number" value={form.imm} onChange={(e) => setForm({ ...form, imm: Number(e.target.value) })} size="small" />
        <TextField label="IPC (%)" type="number" value={form.ipc} onChange={(e) => setForm({ ...form, ipc: e.target.value === "" ? null : Number(e.target.value) })} size="small" />
        <Box sx={{ gridColumn: "1 / 3" }}>
          <Typography variant="caption" color="text.secondary">
            Al guardar, queda marcado como MANUAL — el cron diario no lo sobreescribirá.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={() => onSubmit({ ...form, ipc: form.ipc === "" ? null : form.ipc })}>Guardar (MANUAL)</Button>
      </DialogActions>
    </Dialog>
  );
}

export function IndicadoresView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [editing, setEditing] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());

  const load = async (y = year) => {
    setLoading(true);
    try {
      const res = await IndicatorsService.list({ year: y });
      setItems(res.items || []);
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const handleSubmit = async (payload) => {
    try {
      await IndicatorsService.upsertManual(editing.period.year, editing.period.month, payload);
      setFeedback({ kind: "ok", msg: "Indicadores guardados como MANUAL" });
      setEditing(null);
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await IndicatorsService.sync();
      setFeedback({ kind: "ok", msg: `Sincronizado: ${res.result?.source || "OK"}` });
      await load();
    } catch (e) {
      setFeedback({ kind: "error", msg: e?.response?.data?.error || e.message });
    } finally { setSyncing(false); }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, alignItems: "center", flexWrap: "wrap", gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>Indicadores económicos</Typography>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Select size="small" value={year} onChange={(e) => setYear(e.target.value)}>
            {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </Select>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleSync} disabled={syncing}>
            {syncing ? <CircularProgress size={16} /> : "Sincronizar mindicador.cl"}
          </Button>
        </Box>
      </Box>

      {feedback && <Alert severity={feedback.kind === "ok" ? "success" : "error"} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>{feedback.msg}</Alert>}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Los indicadores se sincronizan automáticamente cada día a las 06:00 desde mindicador.cl.
        Puedes editar manualmente para sobreescribir un mes específico (queda marcado como MANUAL y no se vuelve a sobreescribir).
      </Typography>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Período</TableCell>
                <TableCell align="right">UF</TableCell>
                <TableCell align="right">UTM</TableCell>
                <TableCell align="right">UTA</TableCell>
                <TableCell align="right">IMM</TableCell>
                <TableCell align="right">IPC</TableCell>
                <TableCell>Fuente</TableCell>
                <TableCell>Actualizado</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3 }}><CircularProgress size={20} /></TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center" sx={{ py: 3, color: "text.secondary" }}>Sin indicadores cargados aún</TableCell></TableRow>
              ) : items.map((i) => (
                <TableRow key={i._id} hover>
                  <TableCell>{MONTHS[i.period.month - 1]} {i.period.year}</TableCell>
                  <TableCell align="right">{fmtN(i.uf)}</TableCell>
                  <TableCell align="right">{fmtN(i.utm)}</TableCell>
                  <TableCell align="right">{fmtN(i.uta)}</TableCell>
                  <TableCell align="right">{fmtN(i.imm)}</TableCell>
                  <TableCell align="right">{i.ipc != null ? `${i.ipc}%` : "—"}</TableCell>
                  <TableCell><Chip size="small" label={i.source} color={SOURCE_COLORS[i.source] || "default"} /></TableCell>
                  <TableCell>{i.fetchedAt ? new Date(i.fetchedAt).toLocaleString("es-CL") : "—"}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => setEditing(i)}><EditIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <EditDialog open={!!editing} onClose={() => setEditing(null)} indicator={editing} onSubmit={handleSubmit} />
    </Box>
  );
}
